import {TZDate} from '@date-fns/tz'
import Big from 'big.js'
import {addMinutes} from 'date-fns'
import {Breadcrumb, Breadcrumbs} from 'react-aria-components'
import zipToTz from 'zip-to-tz'

import PhoneSvg from '@/assets/homepage/icon/telephone-call.svg?react'
import DeliveredSvg from '@/assets/icons/delivered.svg?react'
import DeliveringSvg from '@/assets/icons/delivering.svg?react'
import LocationSvg from '@/assets/icons/location.svg?react'
import PlacedSvg from '@/assets/icons/placed.svg?react'
import PreparingSvg from '@/assets/icons/preparing.svg?react'
import starSvg from '@/assets/icons/star/full.svg'
import StoreSvg from '@/assets/icons/store.svg?react'
import Price from '@/components/Price'
import SkipLink from '@/components/SkipLink'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import Navbar from '@/layout/Navbar'
import {useFoodOrder} from '@/lib/food/hooks/useFoodOrder'
import {useFoodTracking} from '@/lib/food/hooks/useFoodTracking'
import useStoreDetails from '@/lib/food/hooks/useStoreDetails'
import HeadTags from '@/lib/head/HeadTags'
import {FoodOrderRoute, FoodOrdersRoute, FoodRoute, FoodTrackingRoute} from '@/routeRegistry'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import RACLink from '@/utils/router/RACLink'

import style from './FoodTracking.module.scss'

const Status = {
  Placed: 'Placed',
  Preparing: 'Preparing',
  Delivering: 'Delivering',
  Delivered: 'Delivered',
} as const
type Status = (typeof Status)[keyof typeof Status]

const StatusText: Record<Status, string> = {
  Placed: 'Processing',
  Preparing: 'Preparing',
  Delivering: 'Delivering',
  Delivered: 'Completed',
}

const STATUSES = [Status.Placed, Status.Preparing, Status.Delivering, Status.Delivered]

export default function FoodTracking() {
  const {foodOrderId} = FoodTrackingRoute.useParams()

  const {data: tracking} = useFoodTracking(foodOrderId)

  const {data: order} = useFoodOrder(foodOrderId)

  const {data: store} = useStoreDetails(order?.extras.storeId)

  const shippingAddress = [
    order?.shippingInfo.line1,
    order?.shippingInfo.line2,
    order?.shippingInfo.city,
    order?.shippingInfo.state,
    order?.shippingInfo.postalCode,
    order?.shippingInfo.country,
  ]
    .filter(Boolean)
    .join(', ')

  // const currentStatus: Status = Status.Placed
  // const currentStatus: Status = Status.Preparing
  // const currentStatus: Status = Status.Delivering
  // const currentStatus: Status = Status.Delivered
  const currentStatus = (() => {
    switch (tracking?.status) {
      case undefined:
      case 'unplaced':
      case 'awaiting_confirmation':
        return Status.Placed
      case 'in_progress':
        if (tracking.driverLatitude && tracking.driverLongitude) {
          return Status.Delivering
        }
        return Status.Preparing
      case 'completed':
      case 'canceled':
      case 'failed':
        return Status.Delivered
      default:
        return Status.Placed
    }
  })()
  const completedStatuses = STATUSES.slice(0, STATUSES.indexOf(currentStatus))
  const currentStatusText = (() => {
    switch (currentStatus) {
      case Status.Placed:
        return 'Sending your order...'
      case Status.Preparing:
        return 'Preparing your order...'
      case Status.Delivering:
        return 'Your order is on the way...'
      case Status.Delivered:
        return 'Your order has been delivered'
    }
  })()

  const timezone = zipToTz(order?.shippingInfo.postalCode ?? '')

  const expectedDeliveryMinute = order
    ? Math.round((order.quote.deliveryTimeMin + order.quote.deliveryTimeMax) / 2)
    : 0

  const expectedDeliveryDate = new TZDate(
    addMinutes((tracking?.timestamp ?? 0) * 1000, expectedDeliveryMinute),
    timezone,
  )

  const shouldDisplayFullDate =
    (order?.quote.deliveryTimeMin ?? 0) > 120 || currentStatus === Status.Delivered

  return (
    <>
      <HeadTags title='Food Order Tracking' />
      <SkipLink title='Skip to main content' to='#main-content' />
      <div className={style.Page}>
        <Navbar variant='dark' haveCart={false} />
        <main className={style.Container} {...skipTargetProps('main-content')}>
          <div className={style.BreadcrumbsContainer} role='region' aria-label='breadcrumb'>
            <Breadcrumbs className={style.Breadcrumbs}>
              <Breadcrumb className={style.Breadcrumb}>
                <RACLink className={style.Link} to={FoodRoute.fullPath}>
                  Home
                </RACLink>
              </Breadcrumb>
              <Breadcrumb className={style.Breadcrumb}>
                <RACLink className={style.Link} to={FoodOrdersRoute.fullPath}>
                  Food Orders
                </RACLink>
              </Breadcrumb>
              <Breadcrumb className={style.Breadcrumb}>
                <RACLink
                  className={style.Link}
                  to={FoodTrackingRoute.fullPath}
                  params={{
                    foodOrderId,
                  }}
                >
                  Food Tracking
                </RACLink>
              </Breadcrumb>
            </Breadcrumbs>
          </div>
          <h1 className={style.Heading}>Food Order #{foodOrderId}</h1>
          <div className={style.FoodTracking}>
            <div className={style.Details}>
              <div className={style.Panel}>
                <div className={style.Status}>
                  {StatusText[currentStatus]} (
                  {currentStatus !== Status.Delivered ? 'ETA' : 'Delivered at'}:{' '}
                  {shouldDisplayFullDate &&
                    expectedDeliveryDate.toLocaleString(undefined, {
                      month: 'short',
                      // weekday: 'short',
                      year: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                  )
                </div>
                <div className={style.Description}>{currentStatusText}</div>
                <div>
                  <ol className={style.ProgressTracker}>
                    <li
                      className={clsx(
                        completedStatuses.includes(Status.Placed) && style.IsComplete,
                        currentStatus === Status.Placed && style.IsActive,
                      )}
                    >
                      <div className={style.ProgressContent}>
                        <PlacedSvg />
                      </div>
                      {currentStatus === Status.Placed && (
                        <VisuallyHidden aria-current='step'>Placed</VisuallyHidden>
                      )}
                    </li>
                    <li
                      className={clsx(
                        completedStatuses.includes(Status.Preparing) && style.IsComplete,
                        currentStatus === Status.Preparing && style.IsActive,
                      )}
                    >
                      <div className={style.ProgressContent}>
                        <PreparingSvg />
                      </div>
                      {currentStatus === Status.Preparing && (
                        <VisuallyHidden aria-current='step'>Preparing</VisuallyHidden>
                      )}
                    </li>
                    <li
                      className={clsx(
                        completedStatuses.includes(Status.Delivering) && style.IsComplete,
                        currentStatus === Status.Delivering && style.IsActive,
                      )}
                    >
                      <div className={style.ProgressContent}>
                        <DeliveringSvg />
                      </div>
                      {currentStatus === Status.Delivering && (
                        <VisuallyHidden aria-current='step'>Delivering</VisuallyHidden>
                      )}
                    </li>
                    <li className={clsx(currentStatus === Status.Delivered && style.IsComplete)}>
                      <div className={style.ProgressContent}>
                        <DeliveredSvg />
                      </div>
                      {currentStatus === Status.Delivered && (
                        <VisuallyHidden aria-current='step'>Delivered</VisuallyHidden>
                      )}
                    </li>
                  </ol>
                </div>
              </div>
              <div className={style.Panel}>
                <VisuallyHidden>
                  <h2>Address details:</h2>
                </VisuallyHidden>
                <ol className={style.RouteInfo}>
                  <li className={style.RouteItem}>
                    <VisuallyHidden>Store address:</VisuallyHidden>
                    <StoreSvg className={style.RouteIcon} />
                    <div className={style.RouteContent}>{order?.extras.storeAddress}</div>
                  </li>
                  <li className={style.RouteItem}>
                    <VisuallyHidden>Shipping address:</VisuallyHidden>
                    <LocationSvg className={style.RouteIcon} />
                    <div className={style.RouteContent}>{shippingAddress}</div>
                  </li>
                </ol>
              </div>
              <div className={clsx(style.Panel, style.OrderDetails)}>
                <VisuallyHidden>
                  <h2>Order details:</h2>
                </VisuallyHidden>
                <div className={style.Line1}>
                  <VisuallyHidden>Ordered item(s):</VisuallyHidden>
                  <img src={store?.logo_photos[0]} alt='' />
                  <div>{order?.extras.store}</div>
                </div>
                {order?.extras.totalWithTip && (
                  <div className={style.Line2}>
                    <div>Total:</div>
                    <div>
                      <Price
                        price={Big(order.extras.totalWithTip)
                          .plus(order.moreInfo.amountRoundUp)
                          .minus(order.moreInfo.amountDiscount)}
                        countryOrLocale={order.locale}
                        options={{
                          fractionDigits: USD_DISPLAY_DECIMALS,
                          exactFractionDigits: true,
                        }}
                      />
                    </div>
                  </div>
                )}
                <Link to={FoodOrderRoute.fullPath} params={{foodOrderId}} className={style.Line3}>
                  <div>View order summary</div>
                  <div aria-hidden='true'> → </div>
                </Link>
              </div>
            </div>
            <div className={style.DriverInfo}>
              <div className={style.DriverInfoHeader}>
                <DeliveringSvg className={style.DriverInfoHeaderIcon} />
                <div aria-hidden='true'>Delivery</div>
              </div>
              {tracking?.driverName && (
                <div className={style.DriverInfoContent}>
                  <VisuallyHidden>
                    <h2>Driver details</h2>
                  </VisuallyHidden>
                  <img className={style.DriverInfoImage} src={tracking.driverPhotoUrl} alt='' />
                  <div>
                    <div className={style.DriverInfo1}>
                      <div className={style.DriverInfo1Name}>
                        <VisuallyHidden>Driver name:</VisuallyHidden>
                        {tracking.driverName}
                      </div>
                      {!!tracking.driverRating && (
                        <div className={style.DriverInfo1Rating}>
                          <VisuallyHidden>Driver rating:</VisuallyHidden>
                          {tracking.driverRating} <img src={starSvg} alt='' />
                        </div>
                      )}
                    </div>
                    {(tracking.vehicleType ?? tracking.vehicle) && (
                      <div className={style.DriverInfoVehicle}>
                        <VisuallyHidden>Vehicle details:</VisuallyHidden>
                        {[tracking.vehicleType, tracking.vehicle].filter(Boolean).join(' • ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {tracking?.driverPhone && (
                <a href={`tel:${tracking.driverPhone}`} className={style.DriverInfoCall}>
                  <div className={style.DriverInfoCallItem}>
                    <PhoneSvg className={style.DriverInfoCallIcon} />
                    Call the driver
                  </div>
                  <div className={style.DriverInfoCallItem}>{tracking.driverPhone}</div>
                </a>
              )}
              <div className={style.DeliveryDetails}>
                <h2>
                  Delivery Details<VisuallyHidden>:</VisuallyHidden>
                </h2>
                <div className={style.DeliveryDetailsItem}>
                  <div>
                    Delivery time<VisuallyHidden>:</VisuallyHidden>
                  </div>
                  <div>
                    {[order?.quote.deliveryTimeMin, order?.quote.deliveryTimeMax]
                      .filter(Boolean)
                      .join(' - ')}{' '}
                    mins
                  </div>
                </div>
                <div className={style.DeliveryDetailsItem}>
                  <div>
                    Delivery to<VisuallyHidden>:</VisuallyHidden>
                  </div>
                  <div>
                    {[order?.shippingInfo.name, order?.shippingInfo.phone]
                      .filter(Boolean)
                      .join(' - ')}
                  </div>
                </div>
                <div className={style.DeliveryDetailsItem}>
                  <div>
                    Delivery note<VisuallyHidden>:</VisuallyHidden>
                  </div>
                  <div>{order?.moreInfo.note ?? 'No note'}</div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
